import { useStore } from '@tanstack/react-form';
import { Card, Divider, Heading, Text, TextLink } from '@ui/themed';
import { t } from 'i18next';
import { SETTING_KEY_TO_LABEL } from '../../constants';
import { useCustomerGroupBuilderForm } from '../../hooks/use-customer-group-builder-form';
import { filterSettings } from '../../transformers';
import type { SettingComponentMapKeys } from '../../types/settings.types';

type CustomizedRule = { key: SettingComponentMapKeys; label: string };

export function CustomerGroupBuilderSummaryCard({
  scrollToSection,
}: {
  scrollToSection: (settingkey: SettingComponentMapKeys) => void;
}) {
  const form = useCustomerGroupBuilderForm();

  const settings = useStore(form.store, (state) => state.values.settings);

  const { customizedRules, inheritedRules } = filterSettings(settings).reduce<{
    customizedRules: CustomizedRule[];
    inheritedRules: string[];
  }>(
    (acc, setting) => {
      const label = SETTING_KEY_TO_LABEL[setting.key];

      if (setting.value !== undefined) {
        acc.customizedRules.push({ key: setting.key, label });
      } else {
        acc.inheritedRules.push(label);
      }

      return acc;
    },
    { customizedRules: [], inheritedRules: [] },
  );

  const sections = [
    {
      title: t('features:customer_group.components.customer_group_builder_summary_card.customized'),
      rules: customizedRules,
      isCustomized: true as const,
    },
    {
      title: t('features:customer_group.components.customer_group_builder_summary_card.inherited'),
      rules: inheritedRules,
      isCustomized: false as const,
    },
  ].filter((s) => s.rules.length > 0);

  return (
    <Card>
      <Card.Body>
        {sections.map((section, index) => (
          <div key={section.title} className="flex flex-col gap-y-1.5">
            <Heading level="h2" style="sm" weight="semibold">
              {section.title}
            </Heading>
            {section.isCustomized
              ? section.rules.map((rule) => (
                  <TextLink
                    key={rule.key}
                    variant="secondary"
                    onClick={() => scrollToSection(rule.key)}
                  >
                    {rule.label}
                  </TextLink>
                ))
              : section.rules.map((rule) => (
                  <Text key={rule} variant="secondary">
                    {rule}
                  </Text>
                ))}
            {index === 0 && sections.length > 1 && (
              <div className="mt-1.5">
                <Divider />
              </div>
            )}
          </div>
        ))}
      </Card.Body>
    </Card>
  );
}
